import React from 'react';
import { MotiviewKillerQuestion } from 'typings/target_group';
import { NO_OPTION_ID, optionalQuestionCount } from 'utils/killerQuestions';
import styles from './MotiviewCreateConfirmDialog.module.css';

export interface MotiviewCreateConfirmDialogProps {
    groupName: string;

    allowRegistration: boolean;
    isWebsiteDemo: boolean;
    allowRepeatScan: boolean;
    showDetailsFormBeforeResult: boolean;
    showDetailsFormAfterResult: boolean;
    showCompanyLogo: boolean;
    sendPdfReport: boolean;

    allowMeetingAboveAverage: boolean;
    allowMeetingAverage: boolean;
    allowMeetingBelowAverage: boolean;
    meetingUrls: string[];

    killerQuestionsEnabled: boolean;
    killerQuestions: MotiviewKillerQuestion[];
    killerQuestionsMinCorrect: number;
    killerQuestionsRedirectUrl: string;

    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Final look at a Motiview group before it is created.
 *
 * Shows only what is switched on. Settings left off are simply absent rather
 * than listed as "Off", so the recruiter reads a short description of the group
 * they are about to make instead of scanning a checklist.
 *
 * This is a double check, not a gate: it never blocks and never complains.
 * Anything that genuinely cannot be saved is already caught in the wizard.
 */
export const MotiviewCreateConfirmDialog = ({
    groupName,
    allowRegistration,
    isWebsiteDemo,
    allowRepeatScan,
    showDetailsFormBeforeResult,
    showDetailsFormAfterResult,
    showCompanyLogo,
    sendPdfReport,
    allowMeetingAboveAverage,
    allowMeetingAverage,
    allowMeetingBelowAverage,
    meetingUrls,
    killerQuestionsEnabled,
    killerQuestions,
    killerQuestionsMinCorrect,
    killerQuestionsRedirectUrl,
    onConfirm,
    onCancel,
}: MotiviewCreateConfirmDialogProps) => {
    const enabledSettings = [
        allowRegistration && 'Candidates can register',
        isWebsiteDemo && 'Website version',
        allowRepeatScan && 'Repeated scan allowed',
        showDetailsFormBeforeResult && 'Details form before the result',
        showDetailsFormAfterResult && 'Details form after the result',
        showCompanyLogo && 'Company logo instead of the Motiview logo',
        sendPdfReport && 'Candidates can email themselves the report',
    ].filter(Boolean) as string[];

    const meetingBands = [
        allowMeetingAboveAverage && 'above average',
        allowMeetingAverage && 'average',
        allowMeetingBelowAverage && 'below average',
    ].filter(Boolean) as string[];

    const usableMeetingUrls = meetingUrls.filter((url) => url.trim());
    const showKillerQuestions = killerQuestionsEnabled && killerQuestions.length > 0;
    const optionalCount = optionalQuestionCount(killerQuestions);

    const correctAnswerLabel = (
        question: MotiviewKillerQuestion,
    ): string => {
        if (question.type === 'YES_NO') {
            return question.correct_option_ids[0] === NO_OPTION_ID
                ? 'No'
                : 'Yes';
        }
        return (question.options ?? [])
            .filter((option) => question.correct_option_ids.includes(option.id))
            .map((option) => option.text)
            .join(', ');
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.panel}>
                <h2 className={styles.title}>Create this Motiview group?</h2>
                <p className={styles.lede}>
                    Here is everything that is switched on. Have a quick look
                    before you create the group.
                </p>

                {groupName && (
                    <div className={styles.groupName}>
                        <span className={styles.groupNameLabel}>Group</span>
                        <span>{groupName}</span>
                    </div>
                )}

                {enabledSettings.length > 0 && (
                    <>
                        <h3 className={styles.sectionTitle}>Enabled</h3>
                        <ul className={styles.enabled}>
                            {enabledSettings.map((setting) => (
                                <li key={setting}>{setting}</li>
                            ))}
                        </ul>
                    </>
                )}

                {meetingBands.length > 0 && (
                    <>
                        <h3 className={styles.sectionTitle}>
                            Meeting scheduling
                        </h3>
                        <p className={styles.detail}>
                            Offered to candidates scoring{' '}
                            {meetingBands.join(', ')}.
                        </p>
                        {usableMeetingUrls.length > 0 && (
                            <ul className={styles.urls}>
                                {usableMeetingUrls.map((url) => (
                                    <li key={url}>{url}</li>
                                ))}
                            </ul>
                        )}
                    </>
                )}

                {showKillerQuestions && (
                    <>
                        <h3 className={styles.sectionTitle}>
                            Killer questions
                            <span className={styles.locked}>
                                Cannot be changed later
                            </span>
                        </h3>
                        <ol className={styles.list}>
                            {killerQuestions.map((question) => (
                                <li key={question.id} className={styles.item}>
                                    <div className={styles.questionText}>
                                        {question.text}
                                    </div>

                                    {question.type === 'MULTIPLE_CHOICE' && (
                                        <ul className={styles.options}>
                                            {(question.options ?? []).map(
                                                (option) => (
                                                    <li
                                                        key={option.id}
                                                        className={
                                                            question.correct_option_ids.includes(
                                                                option.id,
                                                            )
                                                                ? styles.optionCorrect
                                                                : styles.option
                                                        }
                                                    >
                                                        {option.text}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    )}

                                    <div className={styles.meta}>
                                        <span className={styles.answer}>
                                            Correct:{' '}
                                            {correctAnswerLabel(question)}
                                        </span>
                                        {question.must_be_correct && (
                                            <span className={styles.required}>
                                                Must be correct
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <div className={styles.rules}>
                            {optionalCount > 0 ? (
                                <p>
                                    Of the {optionalCount} question
                                    {optionalCount === 1 ? '' : 's'} not marked
                                    as required, at least{' '}
                                    <strong>{killerQuestionsMinCorrect}</strong> must
                                    be correct.
                                </p>
                            ) : (
                                <p>
                                    Every question must be answered correctly.
                                </p>
                            )}
                            <p>
                                Rejected candidates are sent to{' '}
                                <strong>{killerQuestionsRedirectUrl}</strong>
                            </p>
                        </div>
                    </>
                )}

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancel}
                        onClick={onCancel}
                    >
                        Go back and edit
                    </button>
                    <button
                        type="button"
                        className={styles.confirm}
                        onClick={onConfirm}
                    >
                        Create the group
                    </button>
                </div>
            </div>
        </div>
    );
};
